// NavigTreeConfigurator.jsx
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Modal,
  Button,
  Group,
  MultiSelect,
  Stack,
  Text,
  Divider,
  ActionIcon,
  Tooltip,
  Badge,
} from '@mantine/core';
import { withTranslation } from 'react-i18next';
import { Plus, Trash, Refresh } from 'tabler-icons-react';

import ObjectActionCreators from '../../../actions.js';
import { parseJson } from '../../../utils/parseJson.js';
import { ToggleLevelIcon } from '../../../constants/icon.js';

/* ========================= Utils ========================= */
const joinPipe = (arr) =>
  Array.isArray(arr) ? arr.filter(Boolean).join('|') : arr || '';

const splitPipe = (val) =>
  typeof val === 'string' && val.length ? val.split('|').filter(Boolean) : [];

/**
 * getAllowedObjects — version multi-parent (union)
 * @param {Array} objectsDefList - [{ key, label, allowUnder, ... }]
 * @param {string} parentPipeVal  ex: 'framework|domains'
 * @returns {string[]} keys autorisées
 */
function getAllowedObjects(objectsDefList, parentPipeVal) {
  if (!Array.isArray(objectsDefList) || !objectsDefList.length) return [];
  const parents = splitPipe(parentPipeVal);
  if (!parents.length) return [];

  const allowFromOneParent = (parentTypeId) => {
    const res = new Set();
    let grew = true;
    while (grew) {
      const before = res.size;
      try {
        objectsDefList.forEach((item) => {
          if (res.has(item.key)) return;
          const allowUnder = item.allowUnder
            ? parseJson(item.allowUnder)
            : null;
          if (!Array.isArray(allowUnder)) return;
          // autorisé si parent direct OU si déjà présent dans res (propagation)
          if (allowUnder.some((trg) => trg === parentTypeId || res.has(trg))) {
            res.add(item.key);
          }
        });
      } catch (e) {
        // ignore parsing errors silently
      }
      grew = res.size !== before;
    }
    return res;
  };

  const union = new Set();
  parents.forEach((p) => {
    allowFromOneParent(p).forEach((k) => union.add(k));
  });
  return Array.from(union);
}

function toDataOption(item, countByType) {
  const c = countByType?.find((x) => x.type === item.key)?.count ?? 0;
  return {
    value: item.key,
    label: c ? `${item.label} (${c})` : item.label,
  };
}

/* ========================= Level Row ========================= */
function LevelRow({
  levelIndex,
  valuePipe,
  setValuePipe,
  objectsDefList,
  parentPipeVal,
  childsCountByType,
  t,
}) {
  const isRoot = levelIndex === 0;
  const dispatch = useDispatch();
  const toggleLevel = useCallback((level) => {
    dispatch(ObjectActionCreators.toggleDisplayMode(level));
  }, []);
  const parentIsEmpty =
    !isRoot && (!parentPipeVal || !splitPipe(parentPipeVal).length);

  // Liste filtrée + tri par count desc
  const allowed = useMemo(
    () => (isRoot ? [] : getAllowedObjects(objectsDefList, parentPipeVal)),
    [isRoot, objectsDefList, parentPipeVal]
  );

  const filteredList = useMemo(() => {
    let base = isRoot
      ? objectsDefList || []
      : (objectsDefList || []).filter((o) => allowed.includes(o.key));

    if (Array.isArray(childsCountByType)) {
      const withCounts = base.map((elem) => {
        const c =
          childsCountByType?.find((e) => e.type === elem.key)?.count ?? 0;
        return { ...elem, count: c };
      });
      withCounts.sort((a, b) => (b.count || 0) - (a.count || 0));
      return withCounts;
    }
    return base;
  }, [isRoot, objectsDefList, allowed, childsCountByType]);

  const data = useMemo(() => {
    const base = filteredList.map((it) => toDataOption(it, childsCountByType));
    return [
      ...base,
      { value: '__ALL__', label: t('app.all') || 'All' },
      { value: '__NONE__', label: t('app.none') || 'None' },
    ];
  }, [filteredList, childsCountByType, t]);

  const selectedValues = splitPipe(valuePipe);

  const handleChange = (arr) => {
    if (arr.includes('__ALL__')) {
      const allReal = filteredList.map((it) => it.key);
      setValuePipe(joinPipe(allReal));
      return;
    }
    if (arr.includes('__NONE__')) {
      setValuePipe('');
      return;
    }
    setValuePipe(joinPipe(arr));
  };

  const handleToggle = () => {
    toggleLevel(levelIndex);
  };

  if (parentIsEmpty) return null; // <-- on cache entièrement le niveau

  return (
    <Group align="flex-start" spacing="sm" noWrap>
      <Badge variant="filled" radius="sm">
        {t('app.level') || 'Level'} {levelIndex}
      </Badge>
      <MultiSelect
        searchable
        clearable
        nothingFound={t('app.no_result') || 'No results'}
        placeholder={t('app.pick_one') || 'Pick one or more'}
        data={data}
        value={selectedValues}
        onChange={handleChange}
        maxDropdownHeight={260}
        limit={100}
        style={{ width: '100%' }}
      />
      <ActionIcon
        variant="default"
        onClick={handleToggle}
        aria-label="Toggle level display"
      >
        <ToggleLevelIcon />
      </ActionIcon>
    </Group>
  );
}

/* ========================= Modal ========================= */
function NavigTreeConfiguratorBase({ opened, onClose, t }) {
  const dispatch = useDispatch();

  const objectsDefList = useSelector((s) => s.objects.objectsDefList) || [];
  const childsCountByTypeMap =
    useSelector((s) => s.objects.childsCountByType) || {};
  const filters = useSelector((s) => s.objects.filters) || {};

  // Construire les niveaux initiaux (level0..levelN) depuis Redux
  const initialLevels = useMemo(() => {
    const arr = [];
    let idx = 0;
    while (true) {
      const v = filters[`level${idx}`];
      if (typeof v === 'undefined') break;
      arr.push(v || '');
      idx += 1;
    }
    if (arr.length === 0) arr.push(''); // au moins level0
    return arr;
  }, [filters]);

  const [levels, setLevels] = useState(initialLevels);

  useEffect(() => {
    setLevels(initialLevels);
  }, [initialLevels, opened]);

  const setLevel = useCallback(
    (levelIndex, pipeVal) => {
      // 1) MAJ state local + coupe les niveaux suivants
      setLevels((prev) => {
        const next = prev.slice(0, levelIndex + 1);
        next[levelIndex] = pipeVal || '';
        return next;
      });

      // 2) MAJ Redux pour le niveau courant
      dispatch(
        ObjectActionCreators.filter_set_level(levelIndex, pipeVal || '')
      );

      // 3) Reset Redux des niveaux suivants
      let i = levelIndex + 1;
      while (true) {
        const key = `level${i}`;
        if (typeof filters[key] === 'undefined') break;
        dispatch(ObjectActionCreators.filter_set_level(i, ''));
        i += 1;
      }
    },
    [dispatch, filters]
  );

  const addLevel = useCallback(() => {
    setLevels((prev) => prev.concat(['']));
    // Pas de dispatch tant qu’aucune sélection n’est faite
  }, []);

  const removeLastLevel = useCallback(() => {
    setLevels((prev) => {
      if (prev.length <= 1) return prev; // au moins level0
      const lastIdx = prev.length - 1;
      dispatch(ObjectActionCreators.filter_set_level(lastIdx, ''));
      return prev.slice(0, lastIdx);
    });
  }, [dispatch]);

  const resetAll = useCallback(() => {
    setLevels(['']); // local
    // Redux: vide tous les niveaux existants
    let idx = 0;
    while (true) {
      const key = `level${idx}`;
      if (typeof filters[key] === 'undefined') break;
      dispatch(ObjectActionCreators.filter_set_level(idx, ''));
      idx += 1;
    }
  }, [dispatch, filters]);

  const renderLevel = (idx) => {
    const parentPipeVal = idx === 0 ? '' : levels[idx - 1] || '';
    return (
      <LevelRow
        key={`level-row-${idx}`}
        levelIndex={idx}
        valuePipe={levels[idx] || ''}
        setValuePipe={(val) => setLevel(idx, val)}
        parentPipeVal={parentPipeVal}
        objectsDefList={objectsDefList}
        childsCountByType={childsCountByTypeMap[idx]}
        t={t}
      />
    );
  };

  const isLevelVisible = useCallback(
    (idx) => idx === 0 || splitPipe(levels[idx - 1]).length > 0,
    [levels]
  );

  const lastVisibleIndex = useMemo(() => {
    let last = -1;
    for (let i = 0; i < levels.length; i += 1) {
      if (isLevelVisible(i)) last = i;
      else break; // dès qu'un niveau n'est plus visible, les suivants ne le seront pas
    }
    return last;
  }, [levels, isLevelVisible]);

  const canAddNext =
    lastVisibleIndex === levels.length - 1 &&
    splitPipe(levels[lastVisibleIndex] || '').length > 0;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('app.configure_hierarchy') || 'Configure hierarchy'}
      size="lg"
      centered
    >
      <Stack spacing="md">
        <Text size="sm" color="dimmed">
          {t('app.hierarchy_help') ||
            'Ajoutez des niveaux. Chaque niveau propose les types autorisés selon la sélection du niveau parent. Modifier un niveau réinitialise les suivants.'}
        </Text>

        <Divider />

        <Stack spacing="sm">{levels.map((_, i) => renderLevel(i))}</Stack>

        <Group position="apart" mt="md">
          <Group>
            <Button
              leftIcon={<Plus size={16} />}
              variant="light"
              onClick={addLevel}
              disabled={!canAddNext}
            >
              {t('app.add_level') || 'Ajouter un niveau'}
            </Button>

            <Tooltip
              label={t('app.remove_last_level') || 'Retirer le dernier niveau'}
            >
              <ActionIcon
                variant="default"
                onClick={removeLastLevel}
                disabled={levels.length <= 1}
              >
                <Trash size={16} />
              </ActionIcon>
            </Tooltip>

            <Tooltip label={t('app.reset_all') || 'Tout réinitialiser'}>
              <ActionIcon variant="default" onClick={resetAll}>
                <Refresh size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>

          <Group>
            <Button variant="default" onClick={onClose}>
              {t('app.close') || 'Fermer'}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}

const NavigTreeConfigurator = withTranslation()(NavigTreeConfiguratorBase);
export default NavigTreeConfigurator;
